'use client'

import { FC, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChatbotUIContext } from '@/context/context'
import { ChatSettings } from '@/types'
import { IconInfoCircle } from '@tabler/icons-react'

import { buildBasePrompt, DEFAULT_SYSTEM_PROMPT } from '@/lib/build-prompt'
import { CHAT_SETTING_LIMITS } from '@/lib/chat-setting-limits'
import { AIInputHelper } from '@/components/ui/ai-input-helper'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Shimmer } from '@/components/ui/shimmer'

import { ModelSelect } from '../models/model-select'
import { AdvancedSettings } from './advanced-settings'
import { Label } from './label'
import { Slider } from './slider'
import { TextareaAutosize } from './textarea-autosize'
import { WithTooltip } from './with-tooltip'
import { useProfile } from '@/hooks/useProfile'
import { canUseGPT5Reasoning } from '@/lib/subscription'

const TEMPERATURE_DESCRIPTION = `
Temperature controls the creativity of AI responses. A higher value (e.g. 0.8) leads to more creative and diverse responses, while a lower value (e.g. 0.2) produces more precise and consistent responses.
`

const CONTEXT_LENGTH_DESCRIPTION = `
Context length determines how much previous conversation the AI considers. Longer context enables more detailed responses with more history but uses more resources. Shorter context is faster and more cost-effective.
`

interface ChatSettingsFormProps {
  chatSettings: ChatSettings
  onChangeChatSettings: (value: ChatSettings) => void
  useAdvancedDropdown?: boolean
  showTooltip?: boolean
  isLoading?: boolean
}

export const ChatSettingsForm: FC<ChatSettingsFormProps> = ({
  chatSettings,
  onChangeChatSettings,
  useAdvancedDropdown = true,
  showTooltip = true,
  isLoading = false
}) => {
  const { profile } = useProfile()
  const { models, selectedAssistant, selectedWorkspace, availableOpenRouterModels } =
    useContext(ChatbotUIContext)
  const { t } = useTranslation()

  if (!profile) return null

  const isSimplePrompt = (prompt: string) => {
    const cleanPrompt = prompt?.trim() || ''
    if (!cleanPrompt) return true
    const sentences = cleanPrompt.replace(/\{[^}]+\}/g, '').match(/[^.!?]+[.!?]+/g) || []
    return sentences.length <= 1
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label>{t('chat.settings.model')}</Label>

        <ModelSelect
          selectedModelId={chatSettings.model}
          onSelectModel={(model) => {
            onChangeChatSettings({ ...chatSettings, model })
          }}
        />
      </div>

      <div className="relative space-y-1">
        <Label>{t('chat.settings.system_prompt')}</Label>

        {isSimplePrompt(chatSettings.prompt) && (
          <div className="absolute right-2 top-7">
            <AIInputHelper
              title={t('chat.settings.generate_system_prompt')}
              inputPlaceholder={t('chat.settings.describe_assistant_behavior')}
              contextDescription={t('chat.settings.system_prompt_context')}
              onPromptGenerated={(generatedPrompt) => {
                onChangeChatSettings({
                  ...chatSettings,
                  prompt: generatedPrompt
                })
              }}
              buttonClassName="h-6 w-6"
            />
          </div>
        )}
        {isLoading ? (
          <div className="space-y-2">
            <Shimmer className="h-4 w-4/5" />
            <Shimmer className="h-4 w-3/4" />
            <Shimmer className="h-4 w-2/3" />
            <Shimmer className="h-4 w-1/2" />
          </div>
        ) : (
          <TextareaAutosize
            className="border-input bg-background border text-sm"
            placeholder={t('chat.settings.system_prompt_placeholder')}
            onValueChange={(prompt) => {
              onChangeChatSettings({ ...chatSettings, prompt })
            }}
            value={chatSettings.prompt}
            minRows={3}
            maxRows={6}
          />
        )}
      </div>

      {useAdvancedDropdown ? (
        <AdvancedSettings>
          <AdvancedContent
            chatSettings={chatSettings}
            onChangeChatSettings={onChangeChatSettings}
            showTooltip={showTooltip}
          />
        </AdvancedSettings>
      ) : (
        <div>
          <AdvancedContent
            chatSettings={chatSettings}
            onChangeChatSettings={onChangeChatSettings}
            showTooltip={showTooltip}
          />
        </div>
      )}
    </div>
  )
}

interface AdvancedContentProps {
  chatSettings: ChatSettings
  onChangeChatSettings: (value: ChatSettings) => void
  showTooltip: boolean
  showOverrideSystemPrompt?: boolean
}

interface InfoIconTooltipProps {
  label: string
}

export const InfoIconTooltip: FC<InfoIconTooltipProps> = ({ label }) => {
  return (
    <WithTooltip
      delayDuration={0}
      display={label}
      trigger={<IconInfoCircle className="cursor-hover:bg-accent" size={16} />}
    />
  )
}

export const AdvancedContent: FC<AdvancedContentProps> = ({
  chatSettings,
  onChangeChatSettings,
  showTooltip,
  showOverrideSystemPrompt = false
}) => {
  const { profile } = useProfile()
  const { selectedAssistant, selectedWorkspace, availableOpenRouterModels, models } =
    useContext(ChatbotUIContext)
  const { t } = useTranslation()

  const isCustomModel = models.some((model) => model.model_id === chatSettings.model)

  useEffect(() => {}, [])

  function findOpenRouterModel(modelId: string) {
    return availableOpenRouterModels.find((model) => model.modelId === modelId)
  }

  const MODEL_LIMITS = CHAT_SETTING_LIMITS[chatSettings.model] || {
    MIN_TEMPERATURE: 0,
    MAX_TEMPERATURE: 1,
    MAX_CONTEXT_LENGTH: findOpenRouterModel(chatSettings.model)?.maxContext || 4096
  }

  const maxContextLength = isCustomModel
    ? models.find((model) => model.model_id === chatSettings.model)?.context_length
    : MODEL_LIMITS.MAX_CONTEXT_LENGTH

  return (
    <div className="mt-2 w-full">
      <div className="space-y-1">
        <Label className="flex items-center justify-between space-x-1">
          <div className={'flex items-center space-x-2 text-nowrap'}>
            <div>{t('chat.settings.temperature')}</div>
            <InfoIconTooltip label={t('chat.settings.temperature_description')} />
          </div>

          <Input
            className={
              'hover:border-input focus:border-input w-[140px] border-transparent text-right'
            }
            min={MODEL_LIMITS.MIN_TEMPERATURE}
            step={'any'}
            max={MODEL_LIMITS.MAX_TEMPERATURE}
            onChange={(e) =>
              onChangeChatSettings({
                ...chatSettings,
                temperature: Math.max(
                  MODEL_LIMITS.MIN_TEMPERATURE,
                  Math.min(parseFloat(e.target.value), MODEL_LIMITS.MAX_TEMPERATURE!)
                )
              })
            }
            value={chatSettings.temperature}
          />
        </Label>

        <Slider
          title={t('chat.settings.temperature')}
          value={[chatSettings.temperature]}
          onValueChange={(temperature) => {
            onChangeChatSettings({
              ...chatSettings,
              temperature: temperature[0]
            })
          }}
          min={MODEL_LIMITS.MIN_TEMPERATURE}
          max={MODEL_LIMITS.MAX_TEMPERATURE}
          step={0.01}
        />
      </div>

      <div className="mt-6 space-y-3">
        <Label className="flex items-center justify-between space-x-1">
          <div className={'flex items-center space-x-2 text-nowrap'}>
            <div>{t('chat.settings.context_length')}</div>
            <InfoIconTooltip label={t('chat.settings.context_length_description')} />
          </div>
          <Input
            className={
              'hover:border-input focus:border-input w-[140px] border-transparent text-right'
            }
            min={0}
            max={maxContextLength}
            step={1}
            pattern={'[0-9]*'}
            onChange={(e) =>
              onChangeChatSettings({
                ...chatSettings,
                contextLength: Math.min(parseInt(e.target.value), maxContextLength!)
              })
            }
            value={chatSettings.contextLength}
          />
        </Label>

        <Slider
          value={[chatSettings.contextLength]}
          onValueChange={(contextLength) => {
            onChangeChatSettings({
              ...chatSettings,
              contextLength: contextLength[0]
            })
          }}
          min={0}
          max={maxContextLength}
          step={1}
        />
      </div>

      {/* Include user context (profile + memories) toggle */}
      <div className="mt-6 space-y-3">
        <Label className="flex items-center justify-between space-x-1">
          <div className={'flex items-center space-x-2 text-nowrap'}>
            <div>{t('Include user context (profile + memories)')}</div>
            <InfoIconTooltip
              label={t(
                'When disabled, your profile context, memories, and preferences will not be included in prompts.'
              )}
            />
          </div>
          <input
            type="checkbox"
            className="size-4"
            checked={!!chatSettings.includeProfileContext}
            onChange={(e) =>
              onChangeChatSettings({
                ...chatSettings,
                includeProfileContext: e.target.checked
              })
            }
          />
        </Label>
      </div>

      {/* GPT-5 Specific Parameters - Only for paid users */}
      {chatSettings.model.startsWith('gpt-5') && canUseGPT5Reasoning(profile) && (
        <>
          <div className="mt-6 space-y-3">
            <Label className="flex items-center space-x-2">
              <div>{t('chat.settings.reasoning_effort')}</div>
              <InfoIconTooltip label="Controls how much reasoning GPT-5 does before responding. Higher effort provides more thorough reasoning but takes longer. Think Deeper mode automatically uses high reasoning." />
            </Label>

            <select
              className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
              value={chatSettings.reasoningEffort || 'none'}
              onChange={(e) =>
                onChangeChatSettings({
                  ...chatSettings,
                  reasoningEffort: e.target.value as 'none' | 'low' | 'medium' | 'high'
                })
              }
            >
              <option value="none">None (Fastest)</option>
              <option value="low">Low</option>
              <option value="medium">Medium (Balanced)</option>
              <option value="high">High (Most Thorough)</option>
            </select>
          </div>

          <div className="mt-6 space-y-3">
            <Label className="flex items-center space-x-2">
              <div>{t('chat.settings.verbosity')}</div>
              <InfoIconTooltip label="Controls how detailed GPT-5's responses are. Lower verbosity gives more concise answers." />
            </Label>

            <select
              className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
              value={chatSettings.verbosity || 'medium'}
              onChange={(e) =>
                onChangeChatSettings({
                  ...chatSettings,
                  verbosity: e.target.value as 'low' | 'medium' | 'high'
                })
              }
            >
              <option value="low">Low (Concise)</option>
              <option value="medium">Medium (Balanced)</option>
              <option value="high">High (Detailed)</option>
            </select>
          </div>
        </>
      )}

      {/* Notice for Free plan usage with GPT-5 models */}
      {chatSettings.model.startsWith('gpt-5') && !canUseGPT5Reasoning(profile) && (
        <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start space-x-3">
            <div className="shrink-0">
              <IconInfoCircle className="size-5 text-blue-400" />
            </div>
            <div className="text-sm">
              <div className="font-medium text-blue-800">GPT-5 Reasoning Restricted</div>
              <div className="mt-1 text-blue-700">
                On the Free plan, GPT-5 uses minimal reasoning for faster, more
                efficient responses.
                <br />
                <span className="font-semibold">
                  Turn on Think Deeper when you want the full reasoning pass.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/*<div className="mt-5">*/}
      {/*  <Label>Embeddings Provider</Label>*/}

      {/*  <Select*/}
      {/*    value={chatSettings.embeddingsProvider}*/}
      {/*    onValueChange={(embeddingsProvider: "openai" | "local") => {*/}
      {/*      onChangeChatSettings({*/}
      {/*        ...chatSettings,*/}
      {/*        embeddingsProvider*/}
      {/*      })*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    <SelectTrigger>*/}
      {/*      <SelectValue defaultValue="openai"/>*/}
      {/*    </SelectTrigger>*/}

      {/*    <SelectContent>*/}
      {/*      <SelectItem value="openai">*/}
      {/*        {profile?.use_azure_openai ? "Azure OpenAI" : "OpenAI"}*/}
      {/*      </SelectItem>*/}

      {/*      {window.location.hostname === "localhost" && (*/}
      {/*        <SelectItem value="local">Local</SelectItem>*/}
      {/*      )}*/}
      {/*    </SelectContent>*/}
      {/*  </Select>*/}
      {/*</div>*/}
    </div>
  )
}
